<script>
  import { onMount, onDestroy } from 'svelte'
  import { link, location } from '../lib/historyNav.js'
  import { checkAuth, logout, replace } from '../api.js'
  import { authUser, paperData, tradingMode, executionInfo, dualAccounts } from '../stores.js'
  import { startPolling, stopPolling } from '../lib/polling.js'
  import { normalizeMode } from '../mode.js'
  import {
    SITE_MODE,
    SITE_ORIGINS,
    defaultPathForSite,
    redirectForSitePath,
    forcedTradingMode,
  } from '../lib/hostSite.js'
  import {
    NAV_GROUPS,
    tabFromPath,
    clientTelegramIdFromPath,
    readNavCollapsed,
    writeNavCollapsed,
  } from '../lib/nav.js'
  import ModeBadge from '../components/ModeBadge.svelte'
  import OfflineBanner from '../components/OfflineBanner.svelte'
  import ThemeToggle from '../components/ThemeToggle.svelte'
  import Overview from './dashboard/Overview.svelte'
  import Brain from './dashboard/Brain.svelte'
  import Trades from './dashboard/Trades.svelte'
  import Live from './dashboard/Live.svelte'
  import Diagnostics from './dashboard/Diagnostics.svelte'
  import Collect from './dashboard/Collect.svelte'
  import Ops from './dashboard/Ops.svelte'
  import Client from './dashboard/Client.svelte'
  import ClientDetail from './dashboard/ClientDetail.svelte'
  import Logs from './dashboard/Logs.svelte'
  import Sessions from './dashboard/Sessions.svelte'

  let collapsed = false

  $: tab = tabFromPath($location || '')
  $: clientTid = clientTelegramIdFromPath($location || '')
  $: tabTitle = (() => {
    for (const g of NAV_GROUPS) {
      const hit = g.items.find((t) => t.id === tab)
      if (hit) return hit.title
    }
    return 'Дашборд'
  })()
  $: documentTitle = (() => {
    if (SITE_MODE === 'live') {
      const ex = $executionInfo || {}
      const bal =
        ex.onchain_pusd_balance != null && Number.isFinite(Number(ex.onchain_pusd_balance))
          ? Number(ex.onchain_pusd_balance)
          : ex.smoke_assumed_bankroll != null && Number.isFinite(Number(ex.smoke_assumed_bankroll))
            ? Number(ex.smoke_assumed_bankroll)
            : null
      if (bal == null) return 'Lunes Crypto · LIVE'
      return `Live $${bal.toFixed(2)} pUSD · Lunes`
    }
    const pairs = Array.isArray($paperData && $paperData.paper_pairs)
      ? $paperData.paper_pairs
      : []
    const withBal = pairs.filter((p) => p && p.balance != null && Number.isFinite(Number(p.balance)))
    if (withBal.length) {
      const total = withBal.reduce((s, p) => s + Number(p.balance), 0)
      return `Paper $${total.toFixed(0)} · ${withBal.length} пар · Lunes`
    }
    const a = $dualAccounts || {}
    const b15 = a['15m'] && a['15m'].balance
    const b5 = a['5m'] && a['5m'].balance
    const has15 = b15 != null && Number.isFinite(Number(b15))
    const has5 = b5 != null && Number.isFinite(Number(b5))
    if (!has15 && !has5) return 'Lunes Crypto'
    const parts = []
    if (has15) parts.push(`15m $${Number(b15).toFixed(2)}`)
    if (has5) parts.push(`5m $${Number(b5).toFixed(2)}`)
    return `Баланс: ${parts.join(' · ')} · Lunes`
  })()
  $: mode = forcedTradingMode(SITE_MODE) || normalizeMode($tradingMode)
  // Live host: show ОСТАНОВЛЕН when kill file is on OR unit is not running.
  $: killStopped = !!(
    mode === 'live' &&
    ($executionInfo?.kill_switch_active || $executionInfo?.live_15m_unit_active === false)
  )
  $: metaBits = (() => {
    const d = $paperData
    const user = (d && d.user) || $authUser || ''
    if (!d) return user || 'Загрузка…'
    const meta = d.meta || {}
    let when = ''
    if (meta.mtime) {
      try {
        when = new Date(meta.mtime * 1000).toLocaleTimeString('ru-RU', { hour12: false })
      } catch {
        when = ''
      }
    }
    return [user, when ? `обновлено ${when}` : ''].filter(Boolean).join(' · ')
  })()

  function toggleNav() {
    collapsed = !collapsed
    writeNavCollapsed(collapsed)
  }

  onMount(async () => {
    collapsed = readNavCollapsed()
    const redir = redirectForSitePath(window.location.pathname || '/', SITE_MODE)
    if (redir) {
      replace(redir)
      return
    }
    const a = await checkAuth()
    if (!a.ok) {
      replace(
        '/login?next=' +
          encodeURIComponent(window.location.pathname || defaultPathForSite(SITE_MODE)),
      )
      return
    }
    authUser.set(a.user)
    startPolling()
  })

  onDestroy(() => {
    stopPolling()
  })
</script>

<OfflineBanner />

<svelte:head>
  <title>{documentTitle}</title>
</svelte:head>

<div class="pm-atmosphere" aria-hidden="true"><div class="pm-atmosphere__grain"></div></div>

<div class="pm-app" class:pm-app--nav-collapsed={collapsed} class:pm-app--brain={tab === 'brain'}>
  <aside class="pm-sidenav" aria-label="Навигация">
    <div class="pm-sidenav__brand">
      {#if !collapsed}
        <p class="pm-eyebrow">lunes</p>
        {#if SITE_MODE === 'paper' || SITE_MODE === 'live'}
          <a class="pm-sidenav__hub" href={SITE_ORIGINS.hub}>← Хаб</a>
        {/if}
        <ModeBadge mode={mode} variant="inline" stopped={killStopped} />
      {:else}
        <span class="pm-sidenav__mark" title="Lunes">L</span>
      {/if}
    </div>

    <button
      type="button"
      class="pm-sidenav__collapse"
      on:click={toggleNav}
      aria-expanded={!collapsed}
      title={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
    >
      {collapsed ? '⟩' : '⟨'}
    </button>

    <nav class="pm-sidenav__nav">
      {#each NAV_GROUPS as g}
        {#if !collapsed && g.title}
          <p class="pm-sidenav__group">{g.title}</p>
        {/if}
        {#each g.items as t}
          <a
            class="pm-sidenav__link"
            class:is-active={tab === t.id}
            class:pm-sidenav__link--live={t.id === 'live'}
            href={t.path}
            use:link
            title={collapsed ? t.title : t.hint || t.title}
          >
            <span class="pm-sidenav__link-title">{collapsed ? t.title.slice(0, 1) : t.title}</span>
            {#if !collapsed && t.id === 'live' && t.hint}
              <span class="pm-sidenav__link-hint">{t.hint}</span>
            {/if}
          </a>
        {/each}
      {/each}
    </nav>

    <div class="pm-sidenav__foot">
      <ThemeToggle variant="nav" />
      <button type="button" class="pm-btn pm-btn--ghost pm-sidenav__logout" on:click={() => logout()}>
        {collapsed ? '⎋' : 'Выйти'}
      </button>
    </div>
  </aside>

  <div class="pm-main">
    <header class="pm-main__head">
      <h1 class="pm-title">{tabTitle}</h1>
      {#if metaBits}
        <p class="pm-meta">{metaBits}</p>
      {/if}
    </header>

    <div class="pm-main__body" class:pm-main__body--brain={tab === 'brain'}>
      {#if tab === 'overview'}
        <Overview />
      {:else if tab === 'brain'}
        <Brain />
      {:else if tab === 'trades'}
        <Trades />
      {:else if tab === 'live'}
        <Live />
      {:else if tab === 'diagnostics'}
        <Diagnostics />
      {:else if tab === 'collect'}
        <Collect />
      {:else if tab === 'ops'}
        <Ops />
      {:else if tab === 'client'}
        {#if clientTid}
          {#key clientTid}
            <ClientDetail telegramId={clientTid} />
          {/key}
        {:else}
          <Client />
        {/if}
      {:else if tab === 'logs'}
        <Logs />
      {:else if tab === 'sessions'}
        <Sessions />
      {/if}
    </div>
  </div>
</div>
